import { useRef, useEffect, useCallback, useState } from 'react';
import type { VoicePermissionErrorType } from '@pages/chatbot/components/VoicePermissionModal';

declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

/** Loại lỗi micro để UI chọn dialog phù hợp */
export type MicErrorCode = 'not-allowed' | 'not-found' | 'in-use' | 'unsupported' | 'unknown';

interface UseSpeechToTextOptions {
  onResult: (text: string) => void;
  onSilenceTimeout?: () => void;
  lang?: string;
  silenceTimeoutMs?: number;
  maxDurationMs?: number;
  onError?: (message: string, code?: MicErrorCode) => void;
  onErrorType?: (type: VoicePermissionErrorType) => void;
}

interface UseSpeechToTextReturn {
  start: () => void;
  stop: () => void;
  stopAndDiscard: () => void;
  isListening: boolean;
  isSupported: boolean;
}

const getSpeechErrorMessage = (error: string): string => {
  if (error === 'not-allowed') {
    return 'Bạn chưa có quyền sử dụng micro. Vui lòng bật lại trong browser settings';
  }
  if (error === 'audio-capture') {
    return 'Không tìm thấy microphone trên thiết bị';
  }
  return 'Lỗi microphone';
};

const getMediaErrorMessage = (errName: string): string => {
  if (errName === 'NotAllowedError' || errName === 'PermissionDeniedError') {
    return 'Bạn chưa có quyền sử dụng micro. Vui lòng bật lại trong browser settings';
  }
  if (errName === 'NotFoundError' || errName === 'DevicesNotFoundError') {
    return 'Không tìm thấy microphone trên thiết bị';
  }
  if (errName === 'NotReadableError' || errName === 'TrackStartError') {
    return 'Không thể kích hoạt micro khi một ứng dụng khác đang dùng micro';
  }
  return 'Không thể kích hoạt micro';
};

const mapSpeechErrorToType = (error: string): VoicePermissionErrorType | null => {
  if (error === 'not-allowed') return 'PERMISSION_DENIED';
  if (error === 'audio-capture') return 'MICROPHONE_NOT_FOUND';
  return null;
};

export const mapMediaErrorToType = (errName: string): VoicePermissionErrorType => {
  if (errName === 'NotAllowedError' || errName === 'PermissionDeniedError') {
    return 'PERMISSION_DENIED';
  }
  if (errName === 'NotFoundError' || errName === 'DevicesNotFoundError') {
    return 'MICROPHONE_NOT_FOUND';
  }
  return 'MICROPHONE_NOT_FOUND';
};

export const getMediaErrorCode = (errName: string): MicErrorCode => {
  if (errName === 'NotAllowedError' || errName === 'PermissionDeniedError') return 'not-allowed';
  if (errName === 'NotFoundError' || errName === 'DevicesNotFoundError') return 'not-found';
  if (errName === 'NotReadableError' || errName === 'TrackStartError') return 'in-use';
  return 'unknown';
};

export function useSpeechToText(options: UseSpeechToTextOptions): UseSpeechToTextReturn {
  const {
    onResult,
    onSilenceTimeout,
    lang = 'vi-VN',
    silenceTimeoutMs = 5000,
    maxDurationMs = 60000,
    onError,
    onErrorType,
  } = options;

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const shouldStartRef = useRef(false);
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const silenceTimeoutFiredRef = useRef(false);
  const isAbortingRef = useRef(false);

  const onResultRef = useRef(onResult);
  const onSilenceTimeoutRef = useRef(onSilenceTimeout);
  const onErrorRef = useRef(onError);
  const onErrorTypeRef = useRef(onErrorType);

  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onSilenceTimeoutRef.current = onSilenceTimeout; }, [onSilenceTimeout]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onErrorTypeRef.current = onErrorType; }, [onErrorType]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const clearMaxDurationTimer = useCallback(() => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
  }, []);

  const resetSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      silenceTimeoutFiredRef.current = true;
      onSilenceTimeoutRef.current?.();
      recognitionRef.current?.stop();
    }, silenceTimeoutMs);
  }, [clearSilenceTimer, silenceTimeoutMs]);

  const setupMaxDurationTimer = useCallback(() => {
    if (maxDurationMs <= 0) return;
    maxDurationTimerRef.current = setTimeout(() => {
      silenceTimeoutFiredRef.current = true;
      onSilenceTimeoutRef.current?.();
      recognitionRef.current?.stop();
    }, maxDurationMs);
  }, [maxDurationMs]);

  const handleRecognitionResult = useCallback((event: SpeechRecognitionEvent) => {
    if (silenceTimeoutFiredRef.current) return;
    let fullTranscript = '';
    for (let i = 0; i < event.results.length; i++) {
      fullTranscript += event.results[i][0].transcript;
    }
    onResultRef.current(fullTranscript);
    resetSilenceTimer();
  }, [resetSilenceTimer]);

  const handleRecognitionError = useCallback((event: SpeechRecognitionErrorEvent) => {
    const errStr = (event?.error as string) || '';
    const isAborted = isAbortingRef.current || errStr === 'aborted' || errStr === 'abort';
    isAbortingRef.current = false;
    setIsListening(false);
    clearSilenceTimer();
    clearMaxDurationTimer();
    if (isAborted) return;
    const errorType = mapSpeechErrorToType(errStr);
    if (errorType && errorType !== 'PERMISSION_DENIED') {
      onErrorTypeRef.current?.(errorType);
    }
    const code: MicErrorCode =
      errStr === 'not-allowed' ? 'not-allowed' : errStr === 'audio-capture' ? 'not-found' : 'unknown';
    onErrorRef.current?.(getSpeechErrorMessage(errStr), code);
  }, [clearSilenceTimer, clearMaxDurationTimer]);

  useEffect(() => {
    const SpeechAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechAPI) {
      setIsSupported(false);
      return;
    }
    try {
      const recognition = new SpeechAPI();
      recognition.lang = lang;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => {
        setIsListening(false);
        clearSilenceTimer();
        clearMaxDurationTimer();
      };
      recognition.onresult = handleRecognitionResult;
      recognition.onerror = handleRecognitionError;
      recognition.onspeechend = resetSilenceTimer;
      recognitionRef.current = recognition;
      setIsSupported(true);
    } catch {
      setIsSupported(false);
      recognitionRef.current = null;
    }

    return () => {
      clearSilenceTimer();
      clearMaxDurationTimer();
      shouldStartRef.current = false;
      isAbortingRef.current = true;
      recognitionRef.current?.abort();
    };
  }, [lang, resetSilenceTimer, clearSilenceTimer, clearMaxDurationTimer, handleRecognitionResult, handleRecognitionError]);

  const triggerRecognitionStart = useCallback(() => {
    isAbortingRef.current = false;
    silenceTimeoutFiredRef.current = false;
    recognitionRef.current?.start();
    resetSilenceTimer();
    setupMaxDurationTimer();
  }, [resetSilenceTimer, setupMaxDurationTimer]);

  const handleMediaStreamSuccess = useCallback((stream: MediaStream) => {
    stream.getTracks().forEach((track) => track.stop());
    if (!shouldStartRef.current) return;
    try {
      triggerRecognitionStart();
    } catch (e) {
      console.error('[STT] Start error:', e);
    }
  }, [triggerRecognitionStart]);

  const handleMediaStreamError = useCallback((err: any) => {
    if (!shouldStartRef.current) return;
    const errName = err?.name || '';
    const errType = mapMediaErrorToType(errName);
    if (errType !== 'PERMISSION_DENIED') {
      onErrorTypeRef.current?.(errType);
    }
    onErrorRef.current?.(getMediaErrorMessage(errName), getMediaErrorCode(errName));
  }, []);

  const start = useCallback(() => {
    const SpeechAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechAPI || !navigator.mediaDevices?.getUserMedia || !recognitionRef.current) {
      onErrorTypeRef.current?.('BROWSER_NOT_SUPPORTED');
      onErrorRef.current?.('Trình duyệt không hỗ trợ ghi âm', 'unsupported');
      return;
    }
    if (!isListening) {
      shouldStartRef.current = true;
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(handleMediaStreamSuccess)
        .catch(handleMediaStreamError);
    }
  }, [isListening, handleMediaStreamSuccess, handleMediaStreamError]);

  const stop = useCallback(() => {
    shouldStartRef.current = false;
    clearSilenceTimer();
    try { recognitionRef.current?.stop(); } catch (e) {}
    setIsListening(false);
  }, [clearSilenceTimer]);

  const stopAndDiscard = useCallback(() => {
    shouldStartRef.current = false;
    silenceTimeoutFiredRef.current = true;
    isAbortingRef.current = true;
    clearSilenceTimer();
    clearMaxDurationTimer();
    try { recognitionRef.current?.abort(); } catch (e) {}
    setIsListening(false);
  }, [clearSilenceTimer, clearMaxDurationTimer]);

  return { start, stop, stopAndDiscard, isListening, isSupported };
}

export default useSpeechToText;
